import {
  type LucideIcon,
  Building2,
  LineChart,
  Plug,
  ScrollText,
  ShieldCheck,
  Zap,
  FileText,
  ShoppingCart,
  Receipt,
  CreditCard,
  Check,
  Layers,
  Share2,
  Sparkles,
  Users,
  Workflow,
  FolderOpen,
  Database,
  GitBranch
} from 'lucide-react';
import { Badge, Button, Card, Hero, Section, StatBar } from '../components/ui';

type CoreFeature = {
  title: string;
  description: string;
  features: string[];
  badge: string;
  icon: LucideIcon;
};

const coreFeatures: CoreFeature[] = [
  {
    title: 'Visual Workflow Builder',
    description:
      'Design custom extraction pipelines with drag-and-drop nodes. Configure inputs, extraction rules, master data mappings, and outputs visually.',
    features: [
      'Drag-and-drop workflow canvas',
      'Input, Extract, Map, and Output nodes',
      'Configure fields and table extraction',
      'Save and reuse workflows across folders'
    ],
    badge: 'New',
    icon: Workflow
  },
  {
    title: 'Drive: Batch Processing',
    description:
      'Upload documents to folders, assign workflows, and process hundreds of files with a single click. Monitor real-time progress.',
    features: [
      'Folder-based document organization',
      'Bulk upload with drag & drop',
      'One-click folder processing',
      'Real-time batch job progress tracking'
    ],
    badge: 'New',
    icon: FolderOpen
  },
  {
    title: 'User-Defined Master Tables',
    description:
      'Upload your own CSV/XLS catalogs with up to 1000 rows. Select mappable fields for automatic data enrichment during extraction.',
    features: [
      'CSV/Excel file import (up to 1000 rows)',
      'Select any column as mappable field',
      'Multiple master tables per organization',
      'Automatic lookup during workflow execution'
    ],
    badge: 'New',
    icon: Database
  },
  {
    title: 'Intelligent Document Processing',
    description:
      'Advanced OCR and LLM-powered extraction that understands document context and structure.',
    features: [
      'Multi-format support (PDF, PNG, JPEG, TIFF)',
      'Automatic field detection and extraction',
      'Table and line-item recognition',
      'Multi-page document handling'
    ],
    badge: 'Core',
    icon: Sparkles
  },
  {
    title: 'Dynamic Output Generation',
    description:
      'Generate structured outputs combining extracted fields and enriched master data. Schema adapts to your workflow configuration.',
    features: [
      'Auto-generated output schema',
      'Merged extraction + master data',
      'JSON files saved alongside PDFs',
      'Export to Google Sheets, webhooks, or D1'
    ],
    badge: 'Core',
    icon: GitBranch
  },
  {
    title: 'Human-in-the-Loop Review',
    description:
      'Streamlined review interface for handling exceptions and validating extracted data.',
    features: [
      'Flagged field highlighting',
      'Side-by-side document comparison',
      'Bulk approval workflows',
      'Audit trail for all changes'
    ],
    badge: 'Core',
    icon: Users
  }
];

type AdvancedFeature = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const advancedFeatures: AdvancedFeature[] = [
  {
    title: 'Workflow Execution Engine',
    description: 'Runtime engine interprets workflow definitions, executes extraction, maps master data, and generates outputs automatically.',
    icon: Zap
  },
  {
    title: 'Batch Job Management',
    description: 'Track batch processing jobs with detailed progress: queued, processing, completed, and failed document counts.',
    icon: LineChart
  },
  {
    title: 'Nested Folder Hierarchy',
    description: 'Organize documents in unlimited folder depth. Process entire folder trees recursively with workflow inheritance.',
    icon: Layers
  },
  {
    title: 'Multi-Tenant Architecture',
    description: 'Complete data isolation with organization-specific configurations, workflows, and master tables.',
    icon: Building2
  },
  {
    title: 'RESTful API',
    description: 'Comprehensive REST API for workflows, folders, documents, master tables, and batch processing.',
    icon: Plug
  },
  {
    title: 'Role-Based Access Control',
    description: 'Granular permissions for owner, admin, manager, analyst, and reviewer roles across all features.',
    icon: ShieldCheck
  },
  {
    title: 'Audit Logging',
    description: 'Complete audit trail of all workflow executions, document operations, and data changes.',
    icon: ScrollText
  },
  {
    title: 'JSON Result Storage',
    description: 'Extraction results saved as .json files alongside originals in R2. View, download, and re-process anytime.',
    icon: FileText
  }
];

type Solution = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const solutions: Solution[] = [
  {
    title: 'Accounts Payable Automation',
    description:
      'Eliminate manual invoice processing with automated extraction, three-way matching, and direct ERP posting.',
    icon: Receipt
  },
  {
    title: 'Procurement Processing',
    description:
      'Streamline purchase orders and requisitions with automated validation against contracts and catalogs.',
    icon: ShoppingCart
  },
  {
    title: 'Expense Management',
    description:
      'Capture receipts automatically, enforce policy rules, and accelerate reimbursements.',
    icon: CreditCard
  },
  {
    title: 'Document Intelligence',
    description:
      'Extract structured data from any financial document including statements and remittances.',
    icon: FileText
  }
];

type Integration = {
  name: string;
  category: string;
  status: 'available' | 'beta';
};

const keyIntegrations: Integration[] = [
  { name: 'SAP', category: 'ERP', status: 'available' },
  { name: 'Oracle NetSuite', category: 'ERP', status: 'available' },
  { name: 'Microsoft Dynamics 365', category: 'ERP', status: 'available' },
  { name: 'QuickBooks Online', category: 'Accounting', status: 'available' },
  { name: 'Sage Intacct', category: 'ERP', status: 'beta' },
  { name: 'Slack', category: 'Communication', status: 'available' },
  { name: 'Microsoft Teams', category: 'Communication', status: 'beta' },
  { name: 'Zapier', category: 'Automation', status: 'available' }
];

const featureStats = [
  { label: 'Batch processing capacity', value: '1000+ docs' },
  { label: 'Custom workflows', value: 'Unlimited' },
  { label: 'Master table rows', value: '1000/table' }
];

export const Features = () => {
  return (
    <>
      <Hero
        title="Enterprise Document Intelligence Platform"
        description="Comprehensive features for automating AP, procurement, expense management, and financial document workflows."
        size="sm"
      >
        <StatBar stats={featureStats} divider />
      </Hero>

      <Section title="Core Capabilities" subtitle="Everything you need to automate document processing" centered>
        <div className="features-core-grid">
          {coreFeatures.map((feature) => (
            <Card key={feature.title} elevation="lg" size="lg" className="features-core-card">
              <div className="features-core-card__top">
                <span className="features-core-card__icon" aria-hidden>
                  <feature.icon size={28} strokeWidth={1.75} />
                </span>
                <div className="features-core-card__header">
                  <h3>{feature.title}</h3>
                  <Badge variant="primary" appearance="soft">
                    {feature.badge}
                  </Badge>
                </div>
                <p className="features-core-card__description">{feature.description}</p>
              </div>
              <ul className="features-core-card__list">
                {feature.features.map((item) => (
                  <li key={item}>
                    <Check size={16} strokeWidth={1.75} aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Advanced Features" subtitle="Enterprise-grade capabilities" background="subtle" centered>
        <div className="features-advanced-grid">
          {advancedFeatures.map((feature) => (
            <Card key={feature.title} elevation="md" className="features-advanced-card">
              <div className="features-advanced-card__icon" aria-hidden>
                <feature.icon size={32} strokeWidth={1.75} />
              </div>
              <div className="features-advanced-card__body">
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Solutions for Every Finance Function" subtitle="Industry-tested use cases" centered>
        <div className="features-advanced-grid">
          {solutions.map((solution) => (
            <Card key={solution.title} elevation="lg" className="features-advanced-card">
              <div className="features-advanced-card__icon" aria-hidden>
                <solution.icon size={32} strokeWidth={1.75} />
              </div>
              <div className="features-advanced-card__body">
                <h4>{solution.title}</h4>
                <p>{solution.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Integrations" subtitle="Connect to your entire tech stack" background="subtle" centered>
        <Card elevation="lg" size="lg" className="integration-card">
          <div className="integration-card__header">
            <h3>Pre-built connectors</h3>
            <p>Native integrations with leading ERP systems, accounting platforms, and workflow tools.</p>
          </div>

          <div className="integration-grid">
            {keyIntegrations.map((integration) => (
              <div key={integration.name} className="integration-item">
                <Check size={16} strokeWidth={1.75} aria-hidden />
                <div className="integration-item__body">
                  <span className="integration-item__name">{integration.name}</span>
                  <span className="integration-item__category">{integration.category}</span>
                </div>
                <Badge variant={integration.status === 'available' ? 'success' : 'warning'} appearance="soft">
                  {integration.status === 'available' ? 'Available' : 'Beta'}
                </Badge>
              </div>
            ))}
          </div>

          <div className="integration-card__footer">
            <h4>RESTful API</h4>
            <p>
              Build custom integrations with comprehensive REST API coverage, webhook notifications, and SDKs for Node,
              Python, Java, and .NET.
            </p>
          </div>
        </Card>
      </Section>

      <Section title="Ready to Get Started?" centered>
        <div className="feature-cta">
          <Button variant="primary" size="lg" as="a" href="/contact">
            Request a demo
          </Button>
          <Button variant="outline" size="lg" as="a" href="/pricing">
            View pricing
          </Button>
        </div>
      </Section>
    </>
  );
};
